/**
 * Shared utility functions for contact/chat sorting
 * Used by reducers, selectors, and thunks
 */

/**
 * Check if a contact/chat entry has a real name (not just a phone number)
 * Used for sorting contacts - entries with real names come before number-only entries
 */
export const hasRealName = (item: any): boolean => {
  const name = item?.name || item?.pushname || item?.formattedTitle || item?.notify || item?.shortName || '';
  if (!name) return false;
  // Check if name is just digits and common phone chars
  const cleanedName = name.replace(/[\s\-\+\(\)]/g, '');
  return !/^\d+$/.test(cleanedName);
};

/**
 * Sort group for contact list ordering
 * Returns 0 for chats with name, 1 for contacts with name, 2 for entries without name
 */
export const getSortGroup = (item: any): number => {
  const itemHasName = hasRealName(item);
  const isChat = item._isChat || item.isChat;
  if (itemHasName && isChat) return 0;      // Chats with name first
  if (itemHasName && !isChat) return 1;     // Contacts with name second
  return 2;                                  // No name last
};

/**
 * Sort contacts by group (chats first, then contacts, then no-name entries)
 * Within each group, sort alphabetically by name
 */
export const sortContactsByGroupThenName = <T extends { name?: string; shortName?: string; pushname?: string }>(
  contacts: T[]
): T[] => {
  try {
    return contacts.slice().sort((a, b) => {
      const groupA = getSortGroup(a);
      const groupB = getSortGroup(b);

      // Different groups: sort by group
      if (groupA !== groupB) return groupA - groupB;

      // Same group: sort alphabetically by name
      const aName = (a as any).name || (a as any).shortName || (a as any).pushname || '';
      const bName = (b as any).name || (b as any).shortName || (b as any).pushname || '';
      return aName.localeCompare(bName);
    });
  } catch (err) {
    return contacts;
  }
};
